import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';
import { useIntl } from '../../../../util/reactIntl';
import { isAdmin } from '../../../../util/data';
import { ADMIN_LANDING_PAGE, isAdminPage } from '../../../../util/adminPages';
import { getPendingListingsCount } from '../../../../util/api';
import { profilePageParams } from '../../../../util/userHelpers';

import { ExternalLink, InlineTextButton, NamedLink } from '../../../../components';

import { userMenuItems, userMenuLogout } from '../navigationConfig';
import {
  aboutColumn,
  helpColumn,
  howItWorksColumn,
  resolveLinkProps,
  trustColumn,
} from '../../../FooterContainer/footerConfig';

import MobileCategoryTree from './MobileCategoryTree';

import css from './TopbarMobileMenu.module.css';

const PLACEHOLDER_ROUTE_NAME = 'LandingPage';

/**
 * Mobile menu content rendered inside the parent Modal.
 *
 * The Modal owns the chrome (overlay, scroll layer, close button at the
 * top right). This component just contributes the link sections + the
 * primary action button at the bottom.
 *
 * Sections, matching the desktop site entry for entry, in its order and with
 * its names:
 * - The categories and Butikker, as in the desktop category row.
 * - When signed in, "Mine sider": the desktop avatar menu, from "Lag annonse"
 *   to "Logg ut". Administrators also get the one "Admin" entry with the
 *   number of listings waiting for review, as on desktop.
 * - The footer's four columns (Om Zuprize, Slik fungerer det, Tillit, Hjelp),
 *   read from the footer's own config so the two can't drift apart.
 *
 * Bottom bar: "Logg inn" for guests only.
 *
 * Closing/opening is owned by the parent Topbar — link clicks call
 * `onClose` so the menu dismisses after the user navigates.
 *
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {boolean} [props.isOpen] - whether the menu is showing; the admin count is fetched on open
 * @param {Object} [props.currentUser]
 * @param {Function} props.onClose
 * @param {Function} [props.onLogout]
 * @param {string} [props.currentPage] - route name of the page the menu opened on
 * @returns {JSX.Element}
 */
const TopbarMobileMenu = props => {
  const { isAuthenticated, isOpen = false, currentUser, onClose, onLogout, currentPage } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const showAdminItems = isAuthenticated && isAdmin(currentUser);
  const [pendingCount, setPendingCount] = useState(null);

  // Same as the desktop menu: asked when an admin opens the menu, never for
  // anyone else. A failed count just leaves the badge off.
  useEffect(() => {
    if (!isOpen || !showAdminItems) {
      return undefined;
    }
    let cancelled = false;
    getPendingListingsCount()
      .then(response => {
        if (!cancelled && typeof response?.count === 'number') {
          setPendingCount(response.count);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, showAdminItems]);

  // currentPage is the route name string (e.g. 'AboutPage') that
  // Topbar propagates from the LayoutComposer. Matching it against
  // each menu item's `name` flips the .sectionLinkActive accent on
  // whichever explore / account link points at the active page —
  // simpler and more reliable than re-deriving the active route
  // from the URL pathname.
  const isActiveLink = routeName => Boolean(routeName) && currentPage === routeName;
  // The one Admin entry stands for the whole panel, so it reads as selected on
  // any page inside it, not only the one it links to.
  const isOnAdminPage = isAdminPage(currentPage);

  const handleLinkClick = () => onClose?.();
  const handleLogout = () => {
    onClose?.();
    onLogout?.();
  };

  const categories = config?.categoryConfiguration?.categories || [];

  return (
    <nav className={css.root} aria-label={intl.formatMessage({ id: 'Topbar.mobileMenuAriaLabel' })}>
      <div className={css.linkSections}>
        <section className={classNames(css.linkSection, css.categorySection)}>
          <MobileCategoryTree categories={categories} onLinkClick={handleLinkClick} />
        </section>

        {isAuthenticated ? (
          <section className={css.linkSection}>
            <h4 className={css.sectionTitle}>
              {intl.formatMessage({ id: 'Topbar.accountSectionTitle' })}
            </h4>
            <div className={css.sectionList}>
              {/* As on Live and in the desktop avatar menu: "Min profil",
                  then "Lag annonse" and the rest (Guro, 9 October). */}
              {currentUser?.id ? (
                <NamedLink
                  className={classNames(css.sectionLink, {
                    [css.sectionLinkActive]: isActiveLink('ProfilePage'),
                  })}
                  name="ProfilePage"
                  params={profilePageParams(currentUser)}
                  onClick={handleLinkClick}
                  aria-current={isActiveLink('ProfilePage') ? 'page' : undefined}
                >
                  {intl.formatMessage({ id: 'Topbar.userMenuViewProfile' })}
                </NamedLink>
              ) : null}
              <NamedLink
                className={classNames(css.sectionLink, {
                  [css.sectionLinkActive]: isActiveLink('NewListingPage'),
                })}
                name="NewListingPage"
                onClick={handleLinkClick}
              >
                {intl.formatMessage({ id: 'Topbar.userMenuCreateListing' })}
              </NamedLink>
              {userMenuItems.map(item => {
                const routeName = item.placeholder ? PLACEHOLDER_ROUTE_NAME : item.name;
                const isActive = !item.placeholder && isActiveLink(routeName);
                return (
                  <NamedLink
                    key={item.key}
                    className={classNames(css.sectionLink, {
                      [css.sectionLinkActive]: isActive,
                    })}
                    name={routeName}
                    params={item.params}
                    onClick={handleLinkClick}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {intl.formatMessage({ id: item.labelMessageId })}
                  </NamedLink>
                );
              })}
              {showAdminItems ? (
                // One way in, selected anywhere inside the panel: the admin
                // pages carry their own nav along the top, so listing each of
                // them here only made this menu longer.
                <NamedLink
                  className={classNames(css.sectionLink, css.adminLink, {
                    [css.sectionLinkActive]: isOnAdminPage,
                  })}
                  name={ADMIN_LANDING_PAGE}
                  onClick={handleLinkClick}
                  aria-current={isOnAdminPage ? 'page' : undefined}
                >
                  {intl.formatMessage({ id: 'Topbar.userMenuAdmin' })}
                  {pendingCount > 0 ? (
                    <span
                      className={css.countBadge}
                      aria-label={intl.formatMessage(
                        { id: 'Topbar.userMenuAdminListingsCount' },
                        { count: pendingCount }
                      )}
                    >
                      {pendingCount}
                    </span>
                  ) : null}
                </NamedLink>
              ) : null}
              <InlineTextButton
                rootClassName={classNames(css.sectionLink, css.logoutLink)}
                onClick={handleLogout}
              >
                {intl.formatMessage({ id: userMenuLogout.labelMessageId })}
              </InlineTextButton>
            </div>
          </section>
        ) : null}

        {/* The footer's columns, in its order and with its names. */}
        {[aboutColumn, howItWorksColumn, trustColumn, helpColumn].map(column => (
          <section key={column.titleId} className={css.linkSection}>
            <h4 className={css.sectionTitle}>{intl.formatMessage({ id: column.titleId })}</h4>
            <div className={css.sectionList}>
              {column.links.map(link => {
                const label = intl.formatMessage({ id: link.labelMessageId });
                if (link.href) {
                  return (
                    <ExternalLink key={link.key} className={css.sectionLink} href={link.href}>
                      {label}
                    </ExternalLink>
                  );
                }
                const linkProps = resolveLinkProps(link);
                const isActive = !link.placeholder && isActiveLink(linkProps.name);
                return (
                  <NamedLink
                    key={link.key}
                    className={classNames(css.sectionLink, {
                      [css.sectionLinkActive]: isActive,
                    })}
                    {...linkProps}
                    onClick={handleLinkClick}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {label}
                  </NamedLink>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {!isAuthenticated ? (
        <div className={css.bottomBar}>
          <NamedLink className={css.primaryButton} name="SignupPage" onClick={handleLinkClick}>
            {intl.formatMessage({ id: 'Topbar.mobileCtaLogin' })}
          </NamedLink>
        </div>
      ) : null}
    </nav>
  );
};

export default TopbarMobileMenu;
